import { useId, useState, type InputHTMLAttributes } from 'react';
import { useT } from '../i18n/i18n.tsx';
import { Icon } from './Icon';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

/** DESIGN.md §9.1 TextField. Label + input + an optional error line; :focus-visible ring comes
 * from the global rule in base.css, not from anything declared here.
 *
 * MVP-293: a `type="password"` field also gets a reveal toggle. Driven off the type rather than a
 * new prop, so all six password fields in the app (sign-in, the invite form's pair, the account
 * dialog's three) get it in one place and none can drift apart. Nothing is stored: the toggle only
 * swaps the rendered `type`, and the field comes back masked on any remount. */
export function TextField({ label, error, id, className, type, ...rest }: TextFieldProps) {
  const t = useT();
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = error ? `${inputId}-error` : undefined;
  const [revealed, setRevealed] = useState(false);
  const revealable = type === 'password';

  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      <label htmlFor={inputId} className="field__label text-meta">
        {label}
      </label>
      {/* A column flex wrapper, not a plain block: it reproduces exactly the stretch the input used
          to get as a direct child of `.field`, so a field with no toggle lays out as it always did. */}
      <div className="field__control">
        <input
          id={inputId}
          className={['field__input text-body', revealable && 'field__input--revealable'].filter(Boolean).join(' ')}
          type={revealable && revealed ? 'text' : type}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={errorId}
          {...rest}
        />
        {revealable && (
          // `type="button"`: this sits inside a <form> in every one of its callers, and a bare
          // <button> there submits it. No `aria-pressed` on top of the name — the name already
          // states what pressing it will do, and announcing both is two ways of saying one thing.
          <button
            type="button"
            className="field__reveal"
            onClick={() => setRevealed((v) => !v)}
            disabled={rest.disabled}
            aria-label={t(revealed ? 'field.hidePassword' : 'field.revealPassword')}
            aria-controls={inputId}
          >
            <Icon name={revealed ? 'eye-off' : 'eye'} />
          </button>
        )}
      </div>
      {error && (
        <span id={errorId} className="field__error text-meta" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
